'use client';

import { RefObject, useCallback, useEffect, useState } from 'react';

export type OverlayPosition = {
    top: number;
    left: number;
    width: number;
};

export type ResolveWidthArgs = {
    triggerRect: DOMRect;
    viewportWidth: number;
    viewportPadding: number;
};

type UseOverlayPositionArgs = {
    anchorRef: RefObject<HTMLElement | null>;
    isOpen: boolean;
    enabled?: boolean;
    viewportPadding: number;
    verticalOffset: number;
    estimatedHeight: number;
    initialWidth?: number;
    resolveWidth: (args: ResolveWidthArgs) => number;
};

export const useOverlayPosition = ({
    anchorRef,
    isOpen,
    enabled = true,
    viewportPadding,
    verticalOffset,
    estimatedHeight,
    initialWidth = 0,
    resolveWidth,
}: UseOverlayPositionArgs) => {
    const [position, setPosition] = useState<OverlayPosition>({
        top: 0,
        left: 0,
        width: initialWidth,
    });

    const updatePosition = useCallback(() => {
        if (typeof window === 'undefined') return;
        if (!anchorRef.current) return;

        const triggerRect = anchorRef.current.getBoundingClientRect();
        const width = Math.round(
            resolveWidth({
                triggerRect,
                viewportWidth: window.innerWidth,
                viewportPadding,
            }),
        );

        let left = triggerRect.left;
        if (left + width > window.innerWidth - viewportPadding) {
            left = Math.max(viewportPadding, window.innerWidth - width - viewportPadding);
        }

        let top = triggerRect.bottom + verticalOffset;
        if (top + estimatedHeight > window.innerHeight - viewportPadding) {
            top = Math.max(viewportPadding, triggerRect.top - estimatedHeight - verticalOffset);
        }

        const nextTop = Math.round(top);
        const nextLeft = Math.round(left);

        setPosition((prevPosition) => {
            if (prevPosition.top === nextTop && prevPosition.left === nextLeft && prevPosition.width === width) {
                return prevPosition;
            }

            return {
                top: nextTop,
                left: nextLeft,
                width,
            };
        });
    }, [anchorRef, estimatedHeight, resolveWidth, verticalOffset, viewportPadding]);

    useEffect(() => {
        if (!isOpen || !enabled) return;

        updatePosition();

        const onViewportUpdate = () => updatePosition();
        const animationFrameId = window.requestAnimationFrame(onViewportUpdate);

        window.addEventListener('resize', onViewportUpdate);
        window.addEventListener('scroll', onViewportUpdate, true);

        return () => {
            window.cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', onViewportUpdate);
            window.removeEventListener('scroll', onViewportUpdate, true);
        };
    }, [enabled, isOpen, updatePosition]);

    return {
        position,
        updatePosition,
    };
};
